import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CANADA_VIEW_BOUNDS, type FlyController } from './cameraFly';
import { useWallRecenter } from './wallReset';
import { WallMap, type WallMapCounts } from './WallMap';
import { WallMediaZone } from './WallMediaZone';
import { WallTickerZone } from './WallTickerZone';
import { WallChipStrip } from './WallChipStrip';
import { WallResetControl } from './WallResetControl';
import { useElementSize } from './lib/useElementSize';
import {
  isEscalationStale,
  useSelectionBridge,
  useWallEscalation,
  type BridgeSelection,
  type WallTarget,
} from './selectionBridge';
import type { WallIncidentContext } from './wallIncidentArtifacts';
import {
  resolveWallConfig,
  wallTrackTemplates,
  wallZoneStyle,
  type WallConfig,
  type WallZone,
} from './wallConfig';
import type { QueueItem } from './anomalyQueue';
import { useWallQueueItems } from './wallQueueData';
import {
  useWallTour,
  useWallTourCursors,
  WallTourChip,
  WallTourSitrepCard,
  type WallTourState,
} from './WallTourOverlay';
import { newTourCardItems, type WallTourCursors } from './wallTour';
import { wallWmsVisibility } from './layers/wmsOverlays';

/**
 * WALL — composed video-wall, v2 (#269; design-council PART X, BINDING).
 *
 * The wall is a CONFIG-DRIVEN ZONE GRID (`wallConfig.ts`) whose pixel tracks
 * are DERIVED from the measured viewport (`resolveTracks`) — the aspect-fit
 * law: a wall must compose zero-overflow at its declared room aspect(s)
 * (GOC 3:1, PEOC 7:1), so no zone is ever clipped and the ticker is never
 * pushed off-glass. Zones: a centre COP hero, stacked full-bleed media flanks,
 * and the room-wide bottom ticker. NO queue zone, NO mini-map (wall-global —
 * queues live at desks per #264; the ≈2:1 Canada-frame hero IS the national
 * overview).
 *
 * READ-ONLY + AUTO-SYNCED: no interactive controls; hero layers = the four
 * `DEFAULT_VIEW_LAYERS` (derived from the lock). It auto-refreshes, mirrors
 * the operator's pinned feeds (BroadcastChannel), and reacts to a synced
 * selection by flying the hero map (selection bridge). Markers/labels scale
 * ~2× for wall-distance readability; URGENT-RED keeps its colour-law hue.
 */

/** Wall auto-refresh cadence — re-poll the map layers every 30s. */
const WALL_REFRESH_MS = 30_000;
/** Hero map marker emphasis (council Part V "~2×"). */
const HERO_MARKER_SCALE = 2;

export function WallView({
  configName,
  panels,
  flankRows,
  dwellMs,
  blowupRestoreMs = null,
  viewport,
  tourEnabled = true,
  tourMinute = null,
  radarGate = false,
}: {
  configName?: string | null;
  /** PEOC only — monitor count (`?wall=peoc&panels=N`); ignored by other configs. */
  panels?: number | null;
  /** GOC only — stacked tiles per flank (`?wall=goc&flankRows=2`); 3 default. */
  flankRows?: number | null;
  /** Media-reel dwell override (ms) from `?dwell=N` (#222). */
  dwellMs?: number | null;
  /** `?blowup=N` tile blow-up auto-restore override (ms; #273, Ruling 4.3). */
  blowupRestoreMs?: number | null;
  /** Test/debug override for the measured wall size (jsdom can't lay out). */
  viewport?: { width: number; height: number };
  /** `?tour=0` kill-switch (Ruling 2.1) — false disables the hourly risk tour. */
  tourEnabled?: boolean;
  /** `?tourMinute=NN` QA clock seam — pins the minute-of-hour so verification
   *  can force any stop without waiting for :45. */
  tourMinute?: number | null;
  /** `?radar=1` founder-gate QA override (Ruling 1.4e) — shows the radar
   *  overlay for the screenshot acceptance pass. */
  radarGate?: boolean;
}): JSX.Element {
  const config = resolveWallConfig(configName, { panels, flankRows, dwellMs });
  // Active operator escalation (#222) — drives the hero fly, the media-reel pause,
  // and the hero banner. Read once here; each zone consumes it.
  const escalation = useWallEscalation();
  // Liveness for the corner identity tag (PEOC) — the hero map bumps this on
  // every layer refresh; the GOC hero renders its own in-hero identity instead.
  const [lastRefreshMs, setLastRefreshMs] = useState<number>(() => Date.now());

  // The hourly risk tour (#272, PART XI Ruling 2): clock-anchored stops swap
  // the hero LAYER PROFILE only; any unacked urgent suspends the tour (red
  // always wins). Items ride the shared wall queue sweep — the same model the
  // chips + ticker read, so suspension can never disagree with the takeover.
  const queueItems = useWallQueueItems();
  const tour = useWallTour({ enabled: tourEnabled, minuteOverride: tourMinute, items: queueItems });
  const cursors = useWallTourCursors();

  // THE ASPECT-FIT LAW at runtime: measure the wall root and resolve the grid
  // tracks in PIXELS for that exact size — rows/columns always sum to the
  // viewport, so the composition cannot overflow at any aspect. (jsdom and the
  // first pre-measure frame fall back to a same-shape fr grid.)
  const [rootRef, measured] = useElementSize<HTMLDivElement>();
  const size = viewport ?? measured;
  const tracks =
    size.width > 0 && size.height > 0 ? config.resolveTracks(size.width, size.height) : null;
  const templates = tracks
    ? wallTrackTemplates(tracks)
    : {
        gridTemplateColumns: `repeat(${gridColumnCount(config)}, minmax(0, 1fr))`,
        gridTemplateRows: `minmax(0, 1fr) minmax(52px, 5%)`,
      };

  return (
    <div
      ref={rootRef}
      data-testid="wall-view"
      data-config={config.name}
      data-escalated={escalation ? escalation.kind : 'none'}
      // h-full (NOT 100dvh): the wall fills its mount (#root, itself a 100%
      // chain to the viewport on real glass) so the founder QA recipe — force
      // #root to the room's true aspect — sizes the measured grid honestly.
      className="relative grid h-full w-full overflow-hidden bg-slate-950"
      style={templates}
    >
      {config.zones.map((zone) => (
        <div
          key={zone.id}
          data-testid={`wall-zone-${zone.kind}`}
          data-zone-id={zone.id}
          className="relative min-h-0 min-w-0 overflow-hidden"
          style={wallZoneStyle(zone)}
        >
          <WallZoneBody
            zone={zone}
            config={config}
            escalation={escalation}
            onMapRefresh={setLastRefreshMs}
            tour={tour}
            queueItems={queueItems}
            cursors={cursors}
            radarGate={radarGate}
            blowupRestoreMs={blowupRestoreMs}
          />
        </div>
      ))}

      {/* Corner identity tag (PEOC, Ruling 2): a compact overlay at the wall's
          leftmost edge — never a centred header, never a layout row. */}
      {config.identityPlacement === 'corner-tag' && (
        <div
          data-testid="wall-banner"
          className="pointer-events-none absolute left-2 top-2 z-10 flex items-center gap-2 rounded bg-black/70 px-2.5 py-1 backdrop-blur"
        >
          <span className="text-sm font-bold tracking-tight text-slate-100">TSR · WALL</span>
          <span className="text-[11px] text-slate-400">{config.label}</span>
          <LivenessStrip lastRefreshMs={lastRefreshMs} compact />
        </div>
      )}

      {/* Wall-mode RESET (#371): clears operator pins + escalation and returns
          the wall to pure auto-curation. Self-hiding — renders only while the
          wall carries operator-placed feeds. */}
      <WallResetControl />
    </div>
  );
}

/** Column count of a config's grid (for the pre-measure fr fallback). */
function gridColumnCount(config: WallConfig): number {
  let max = 1;
  for (const z of config.zones) {
    const end = Number(z.gridColumn.split('/')[1]?.trim());
    if (Number.isFinite(end)) max = Math.max(max, end - 1);
  }
  return max;
}

function WallZoneBody({
  zone,
  config,
  escalation,
  onMapRefresh,
  tour,
  queueItems,
  cursors,
  radarGate,
  blowupRestoreMs,
}: {
  zone: WallZone;
  config: WallConfig;
  escalation: WallTarget | null;
  onMapRefresh: (ms: number) => void;
  tour: WallTourState;
  queueItems: QueueItem[];
  cursors: WallTourCursors;
  radarGate: boolean;
  blowupRestoreMs: number | null;
}): JSX.Element {
  // Tour SITREP card placement follows each wall's identity grammar (Ruling
  // 2.5): GOC (hero identity) = compact hero-corner overlay; PEOC
  // (corner-tag identity) = the card SEATS as a flank tile for the dwell —
  // floating cards over a strip hero stay forbidden. The newness decision is
  // made HERE (not just inside the card) so a quiet stop never reserves an
  // empty tile seat — nothing new → no card → the full feed window.
  const peocTourCard =
    config.identityPlacement === 'corner-tag' &&
    tour.stop &&
    zone.feedOffset === 0 &&
    newTourCardItems(queueItems, tour.stop, cursors[tour.stop.id] ?? null).length > 0 ? (
      <WallTourSitrepCard
        stop={tour.stop}
        items={queueItems}
        cursors={cursors}
        variant="flank-tile"
      />
    ) : null;

  switch (zone.kind) {
    case 'media':
      return (
        <WallMediaZone
          cols={zone.mediaCols ?? 1}
          rows={zone.mediaRows ?? 1}
          feedOffset={zone.feedOffset ?? 0}
          dwellMs={config.reelDwellMs}
          tourCard={peocTourCard}
          // Blow-up grammar (Ruling 4.2): PEOC (panel grid / corner-tag identity)
          // = 2×2 quad; GOC (derived flanks / hero identity) = full-column.
          blowupMode={config.identityPlacement === 'corner-tag' ? 'quad' : 'column'}
          blowupRestoreMs={blowupRestoreMs ?? undefined}
          // News-fallback gate (#362/#371): PEOC only — the GOC room has its own
          // news TVs, so its flanks must never rotate news into a tile.
          allowsNews={config.allowsNewsFallback}
        />
      );
    case 'ticker':
      // #263 — the wall-only bottom crawl (bulletins + wire headlines, with the
      // URGENT-RED takeover). A zone kind, so it exists ONLY where a config
      // places it — never on the COP.
      return <WallTickerZone />;
    case 'cop-map':
      return (
        <WallHeroZone
          config={config}
          escalation={escalation}
          onMapRefresh={onMapRefresh}
          tour={tour}
          queueItems={queueItems}
          cursors={cursors}
          radarGate={radarGate}
        />
      );
    default:
      return <div />;
  }
}

/** The hero COP map + its overlays: the compact count-chip strip (#268 +
 *  Part II §5 standing-alarm line), in-hero identity (GOC), the escalation
 *  banner (#222), and selection-sync (fly on a broadcast selection). The single
 *  hero is BOTH the national frame AND the excursion surface — it holds the
 *  locked Canada default, runs the hourly risk tour, and frames operator
 *  escalations. */
function WallHeroZone({
  config,
  escalation,
  onMapRefresh,
  tour,
  queueItems,
  cursors,
  radarGate,
}: {
  config: WallConfig;
  escalation: WallTarget | null;
  onMapRefresh: (ms: number) => void;
  tour: WallTourState;
  queueItems: QueueItem[];
  cursors: WallTourCursors;
  radarGate: boolean;
}): JSX.Element {
  const [counts, setCounts] = useState<WallMapCounts>({});
  const [lastRefreshMs, setLastRefreshMs] = useState<number>(() => Date.now());
  const flyControllerRef = useRef<FlyController | null>(null);

  const activeTourProfile = tour.stop?.layerProfile ?? null;

  // Honest radar timestamp (Ruling 1.4d): stamp when the radar field becomes
  // visible (its tiles fetch then — the latest pass) and on each ~6-min
  // self-refresh. The line renders only while radar is on the hero — which, as
  // of the cleared founder gate (default-ON, #273), is ambient by default, plus
  // the weather tour stop's overlays and the ?radar=1 QA override. Computed
  // from the SAME visibility map WallMap mounts, so the stamp can't disagree.
  const radarShown = wallWmsVisibility([
    ...(activeTourProfile?.wmsOverlays ?? []),
    ...(radarGate ? (['geomet-radar'] as const) : []),
  ])['geomet-radar'];
  const [radarRefreshedAt, setRadarRefreshedAt] = useState<number | null>(null);
  useEffect(() => {
    setRadarRefreshedAt(radarShown ? Date.now() : null);
  }, [radarShown]);
  const onWmsOverlayRefresh = useCallback((key: string, atMs: number) => {
    if (key === 'geomet-radar') setRadarRefreshedAt(atMs);
  }, []);

  const onCounts = useCallback(
    (c: WallMapCounts) => {
      setCounts(c);
      const now = Date.now();
      setLastRefreshMs(now);
      onMapRefresh(now);
    },
    [onMapRefresh],
  );

  // Selection sync (read-only): when the COP / panel broadcasts a selection, fly
  // the hero map to it. The wall never broadcasts — it only reacts.
  const onSelect = useCallback((sel: BridgeSelection) => {
    const fly = flyControllerRef.current;
    if (!fly || sel.lat == null || sel.lon == null) return;
    if (sel.kind === 'camera') void fly.flyToCamera(sel.lat, sel.lon);
    else void fly.flyToIncident(sel.lat, sel.lon);
  }, []);
  useSelectionBridge(onSelect);

  // Escalation framing (#222): when an operator escalates a target, the hero flies
  // to + frames it (camera = overlay-dive, incident = ~20km incident viewport).
  // Re-fires only when the escalated id/kind changes so the reel can settle.
  const escId = escalation?.id ?? null;
  const escKind = escalation?.kind ?? null;
  const escLat = escalation?.lat ?? null;
  const escLon = escalation?.lon ?? null;
  const escIncidentId = escalation?.incidentId ?? null;
  useEffect(() => {
    const fly = flyControllerRef.current;
    if (!fly || escId == null || escLat == null || escLon == null) return;
    if (escKind === 'camera') void fly.flyToCamera(escLat, escLon);
    else void fly.flyToIncident(escLat, escLon);
  }, [escId, escKind, escLat, escLon]);

  // #428 — the escalated incident's artifact scope (evac polygons + owned-incident
  // framing), resolved on the wall clock. Only a NON-STALE `kind:'incident'` pin
  // un-suppresses artifacts; a camera / stale (past-TTL, the calm-room auto-clear) /
  // cleared pin → null → the base wall. Mirrors `wallIncidentContext` (the pure,
  // tested resolver) but memoizes on the pin's own fields so a steady escalation
  // keeps a stable identity — MapView's apply-visibility effects key on object
  // identity, so churn would re-fetch the evac layer every wall refresh. `escStale`
  // (Date.now() at render, like WallResetControl) crosses to null at the TTL on the
  // next ~30s wall refresh re-render.
  const escStale = isEscalationStale(escalation, Date.now());
  const incidentArtifacts = useMemo<WallIncidentContext | null>(() => {
    if (escKind !== 'incident' || escId == null || escStale) return null;
    return { incidentId: escIncidentId, lat: escLat, lon: escLon };
  }, [escKind, escId, escIncidentId, escLat, escLon, escStale]);

  // Wall RESET (#392): re-assert the locked Canada frame on the hero. A
  // "send to wall" flew the hero down onto the escalation and nothing flew it
  // back (the tour only swaps layers), so it stayed zoomed after a reset cleared
  // the escalation. The reset control lives in a sibling subtree, so it reaches
  // this hero's fly controller through the same-window recenter event.
  const onRecenter = useCallback(() => {
    const fly = flyControllerRef.current;
    if (!fly) return;
    const { west, south, east, north } = CANADA_VIEW_BOUNDS;
    void fly.flyToBounds(west, south, east, north);
  }, []);
  useWallRecenter(onRecenter);

  return (
    <div className="relative h-full w-full bg-[#0b1020]">
      <WallMap
        markerScale={HERO_MARKER_SCALE}
        autoRefreshMs={WALL_REFRESH_MS}
        onCounts={onCounts}
        flyControllerRef={flyControllerRef}
        tourProfile={activeTourProfile}
        radarGateOpen={radarGate}
        onWmsOverlayRefresh={onWmsOverlayRefresh}
        incidentArtifacts={incidentArtifacts}
      />

      {/* Tour corner chip + the GOC hero-corner SITREP card (Ruling 2.4/2.5)
          — bottom-left, the corner-tag grammar: compact, never a layout row,
          never over the hero centre. PEOC's card seats in the flank instead. */}
      {tour.stop && (
        <div className="absolute bottom-2 left-3 z-10 flex flex-col items-start gap-1.5">
          {config.identityPlacement === 'hero' && (
            <WallTourSitrepCard
              stop={tour.stop}
              items={queueItems}
              cursors={cursors}
              variant="hero-corner"
            />
          )}
          <WallTourChip stop={tour.stop} />
        </div>
      )}

      {/* Honest radar timestamp line (Ruling 1.4d) — renders ONLY while the
          radar raster is on the hero (weather stop / founder-gate QA). */}
      {radarShown && radarRefreshedAt != null && (
        <div
          data-testid="wall-radar-stamp"
          className="pointer-events-none absolute bottom-2 right-3 z-10 rounded bg-black/70 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-slate-300 backdrop-blur"
        >
          Radar · ECCC GeoMet · refreshed{' '}
          {new Date(radarRefreshedAt).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </div>
      )}

      {/* Escalation banner (#222) — calm, static, ONE compact block (a tall
          card would eat a strip-aspect hero): proves the hero is framing an
          operator-promoted target without manufacturing motion. #428 — renders a
          compact incident caption from the escalation payload's `details` (the
          COP packed them; they were previously dead), so the wall states WHAT the
          promoted incident is, not just its title. */}
      {escId != null && (
        <div
          data-testid="wall-hero-escalation"
          className="pointer-events-none absolute left-1/2 top-2 z-10 flex max-w-[90%] -translate-x-1/2 flex-col items-start gap-0.5 rounded-md border border-amber-500/70 bg-slate-950/85 px-3 py-1 backdrop-blur"
        >
          <div className="flex max-w-full items-baseline gap-2">
            <span className="shrink-0 text-[10px] font-semibold uppercase tracking-widest text-amber-300">
              On wall · {escKind === 'camera' ? 'camera' : 'incident'}
            </span>
            <span className="truncate text-sm font-bold text-slate-100">{escalation?.label}</span>
          </div>
          {escalation?.details && escalation.details.length > 0 && (
            <span
              data-testid="wall-hero-escalation-caption"
              className="max-w-full truncate text-[11px] font-medium text-slate-300"
            >
              {escalation.details.map((d) => d.value).join(' · ')}
            </span>
          )}
        </div>
      )}

      {/* Compact count-chip strip (#268 option (a) + the Part II §5
          standing-alarm line) — single row, pinned to the hero's top-left
          corner. Floating CARDS over the hero are forbidden (Ruling 2). */}
      <div className="absolute left-3 top-3 z-10">
        <WallChipStrip mapCounts={{ fire: counts.fire }} />
      </div>

      {/* In-hero identity + liveness (GOC — the ≈2:1 hero has the room; PEOC
          uses the wall-corner tag instead). Proves the wall is live to anyone
          who walks in (operator sim Test 2) without manufacturing motion. */}
      {config.identityPlacement === 'hero' && (
        <div
          // #232: a compact scrim (the same corner-tag legibility grammar PEOC
          // uses) so the identity + liveness read at distance over ANY globe
          // brightness — the subtitle/liveness washed out on the bright Canada
          // frame. Overlay-only: holds the aspect-fit / 2× / motion laws.
          className="pointer-events-none absolute right-3 top-3 z-10 rounded-md bg-slate-950/55 px-3 py-2 text-right backdrop-blur"
          data-testid="wall-banner"
        >
          <p className="text-xl font-bold leading-tight tracking-tight text-slate-100">TSR · WALL</p>
          <p className="text-xs text-slate-300">{config.label}</p>
          <LivenessStrip lastRefreshMs={lastRefreshMs} />
        </div>
      )}
    </div>
  );
}

/** "● SYSTEM LIVE · updated Ns ago" — ticks each second so the wall reads alive. */
function LivenessStrip({
  lastRefreshMs,
  compact = false,
}: {
  lastRefreshMs: number;
  compact?: boolean;
}): JSX.Element {
  const [nowMs, setNowMs] = useState<number>(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const ageS = Math.max(0, Math.round((nowMs - lastRefreshMs) / 1000));
  return (
    <p
      className={`flex items-center justify-end gap-1.5 text-slate-400 ${
        compact ? 'text-[11px]' : 'mt-1 text-xs'
      }`}
      data-testid="wall-liveness"
    >
      <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-emerald-400" />
      {compact ? `live · ${ageS}s · read-only` : `System live · updated ${ageS}s ago · read-only`}
    </p>
  );
}
